// Assign an already recorded event to deal(s) afterwards (ticket 80f5a0d8): PUT /events/{id}/allocations
// replaces the whole allocation set (append-only underneath). An empty set takes the event out of every deal.
import { useState } from 'react';
import { getDeals, putEventAllocations } from '../api/endpoints';
import { useApi } from '../api/hooks';
import type { DealRef, EventRow } from '../api/model';
import { errorText } from '../i18n/reasons.hu';
import { ApiError } from '../api/client';
import { isNegativeDecimal, negateDecimal } from '../lib/decimal';
import { eventTypeText } from '../lib/labels';
import { formatUtc } from '../lib/time';
import { AllocationEditor, newAllocRow, validateAllocations, type AllocRow } from './AllocationEditor';
import { ErrorNotice, Loading } from './Async';
import { Dialog } from './Dialog';

/**
 * Which events can carry an allocation afterwards. Voided events are refused by the API (409 event_voided).
 * A balance snapshot is measured against the booked difference (not its quantity), so it is not offered, as the
 * manual entry form does not. A repay is offered (owner, 2026-10-25: a deal that starts with a borrow ends with
 * its repay); its deal allocation is separate from its loan links (Hitelek), and its weight base is its quantity.
 */
export const canAssignDeal = (e: EventRow): boolean => !e.voided && e.type !== 'balance_snapshot';

/** The quantity an event's allocations are measured against (the API's weight base): |r| for a signed result. */
export const allocationBase = (e: EventRow): string => (e.type === 'result' && isNegativeDecimal(e.quantity) ? negateDecimal(e.quantity) : e.quantity);

const messageOf = (x: unknown): string => (x instanceof ApiError ? errorText(x.status, x.reason) : errorText(null, null));
const asError = (x: unknown): Error => (x instanceof Error ? x : new Error(String(x)));

function EventLine({ event }: { event: EventRow }) {
  return (
    <div>
      <b>{eventTypeText(event)}</b> · {formatUtc(event.occurred_at)} UTC · <span className="mono">{event.quantity} {event.asset}</span>
    </div>
  );
}

export function AssignDealDialog({ event, onClose, onSaved }: { event: EventRow; onClose: () => void; onSaved: () => void }) {
  const deals = useApi((s) => getDeals(s));
  const base = allocationBase(event);
  const [rows, setRows] = useState<AllocRow[]>(() => (event.allocations.length ? event.allocations.map((a) => newAllocRow(a.deal_id, a.quantity)) : [newAllocRow(null, base)]));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<Error | null>(null);

  // Open deals, plus any deal the event is already in (a closed deal may keep its allocation).
  const current = new Map(event.allocations.map((a) => [a.deal_id, a.deal_name ?? '']));
  const options: DealRef[] = [
    ...(deals.data ?? []).filter((d) => d.status === 'open' || current.has(d.id)).map((d) => ({ id: d.id, name: d.name })),
    ...[...current].filter(([id]) => !(deals.data ?? []).some((d) => d.id === id)).map(([id, name]) => ({ id, name })),
  ];
  const v = validateAllocations(rows, base);

  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      await putEventAllocations(event.id, rows.map((r) => ({ deal_id: r.dealId as number, quantity: r.qty })));
      onSaved();
    } catch (x) {
      setErr(asError(x));
      setBusy(false);
    }
  };

  return (
    <Dialog title="Ügylethez rendelés" onClose={onClose}>
      <EventLine event={event} />
      <p style={{ margin: 0 }} className="small muted">
        A megadott sorok <b>felváltják</b> az esemény eddigi allokációját. Ha minden sort törölsz, az esemény egyik ügyletnek sem lesz része.
      </p>
      {deals.error ? <ErrorNotice error={deals.error} onRetry={deals.reload} /> : null}
      {deals.loading && !deals.data ? (
        <Loading label="Ügyletek betöltése…" />
      ) : (
        <AllocationEditor rows={rows} onChange={setRows} deals={options} total={base} asset={event.asset} />
      )}
      {rows.length === 0 ? <div className="small muted" data-testid="assign-empty">Nincs sor: mentés után az esemény nem tartozik egyetlen ügylethez sem.</div> : null}
      {err ? <ErrorNotice error={err} /> : null}
      <div className="row-wrap">
        <button type="button" className="btn primary" disabled={busy || !v.ok || (deals.loading && !deals.data)} onClick={() => void save()}>Mentés</button>
        <button type="button" className="btn" onClick={onClose}>Mégse</button>
      </div>
    </Dialog>
  );
}

interface Outcome {
  event: EventRow;
  error: string | null;
}

/** Bulk: each selected event's FULL quantity goes to one chosen deal, one PUT after the other; a failure does not stop the rest. */
export function BulkAssignDialog({ events, onClose, onDone }: { events: EventRow[]; onClose: () => void; onDone: () => void }) {
  const deals = useApi((s) => getDeals(s));
  const [dealId, setDealId] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [outcomes, setOutcomes] = useState<Outcome[] | null>(null);
  const open = (deals.data ?? []).filter((d) => d.status === 'open');
  const replacing = events.filter((e) => e.allocations.length > 0).length;

  const run = async () => {
    if (dealId === null) return;
    setBusy(true);
    const done: Outcome[] = [];
    for (const e of events) {
      try {
        await putEventAllocations(e.id, [{ deal_id: dealId, quantity: allocationBase(e) }]);
        done.push({ event: e, error: null });
      } catch (x) {
        done.push({ event: e, error: messageOf(x) });
      }
    }
    setOutcomes(done);
    setBusy(false);
  };

  const failed = outcomes?.filter((o) => o.error !== null) ?? [];
  return (
    <Dialog title="Kijelöltek ügylethez rendelése" onClose={outcomes ? onDone : onClose}>
      {outcomes ? (
        <>
          <div role="status" data-testid="bulk-summary">
            {outcomes.length - failed.length} esemény hozzárendelve{failed.length ? `, ${failed.length} nem sikerült` : ''}.
          </div>
          {failed.length ? (
            <ul className="small" role="alert" style={{ margin: 0, paddingLeft: 18 }}>
              {failed.map((o) => (
                <li key={o.event.id}>
                  {eventTypeText(o.event)} · {formatUtc(o.event.occurred_at)} UTC · {o.event.quantity} {o.event.asset}: {o.error}
                </li>
              ))}
            </ul>
          ) : null}
          <div className="row-wrap">
            <button type="button" className="btn primary" onClick={onDone}>Bezárás</button>
          </div>
        </>
      ) : (
        <>
          <p style={{ margin: 0 }}>
            <b>{events.length}</b> kijelölt esemény teljes mennyisége a kiválasztott ügyletre kerül.
            {replacing ? <> Közülük <b>{replacing}</b> már tartozik ügylethez; az allokációjukat ez <b>felváltja</b>.</> : null}
          </p>
          {deals.error ? <ErrorNotice error={deals.error} onRetry={deals.reload} /> : null}
          <div className="field">
            <label htmlFor="bulk-deal">Ügylet</label>
            <select id="bulk-deal" className="input" value={dealId ?? ''} onChange={(e) => setDealId(e.target.value ? Number(e.target.value) : null)}>
              <option value="">Válassz ügyletet…</option>
              {open.map((d) => <option key={d.id} value={d.id}>#{d.id} {d.name}</option>)}
            </select>
          </div>
          <div className="row-wrap">
            <button type="button" className="btn primary" disabled={busy || dealId === null} onClick={() => void run()}>Hozzárendelés</button>
            <button type="button" className="btn" disabled={busy} onClick={onClose}>Mégse</button>
          </div>
        </>
      )}
    </Dialog>
  );
}
